import { createTheme } from "@mui/material/styles";
import {
  BORDER_DARK,
  BRAND_COLOR,
  BRAND_COLOR_DARK,
  BUTTON_RADIUS,
  CARD_BORDER,
  CARD_RADIUS,
  CARD_SHADOW,
  CARD_SHADOW_HOVER,
  DIVIDER,
  ERROR_COLOR,
  ERROR_COLOR_DARK,
  PAGE_BG,
  SUCCESS_COLOR,
  SUCCESS_COLOR_DARK,
  SURFACE,
  TEXT_DISABLED,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  WARNING_COLOR,
  WARNING_COLOR_DARK,
} from "./utils/colors";
import {
  FONT_FAMILY,
  FS_BODY,
  FS_CAPTION,
  FS_HEADING,
  FS_MICRO,
  FS_SECTION,
  FS_SUBTITLE,
  FS_TITLE,
  FW_MEDIUM,
  FW_REGULAR,
  FW_SEMIBOLD,
  LH_BODY,
  LH_HEADING,
} from "./utils/typography";

const theme = createTheme({
  palette: {
    primary: { main: BRAND_COLOR, dark: BRAND_COLOR_DARK },
    // pinned so `color="error"` controls and the ERROR_* tokens are the same
    // red by construction rather than by coincidence
    error: { main: ERROR_COLOR, dark: ERROR_COLOR_DARK },
    success: { main: SUCCESS_COLOR, dark: SUCCESS_COLOR_DARK },
    warning: { main: WARNING_COLOR, dark: WARNING_COLOR_DARK },
    text: {
      primary: TEXT_PRIMARY,
      secondary: TEXT_SECONDARY,
      disabled: TEXT_DISABLED,
    },
    divider: DIVIDER,
    // Spec §5: the page canvas is transparent so the widget blends into the
    // Zoho page behind the iframe, but `paper` — cards, dialogs, menus — stays
    // OPAQUE. Content has to stay readable regardless of what Zoho paints
    // behind us, and floating surfaces would otherwise stack their text onto
    // arbitrary page content.
    background: { default: PAGE_BG, paper: SURFACE },
  },
  typography: {
    fontFamily: FONT_FAMILY,
    fontSize: FS_BODY,
    fontWeightRegular: FW_REGULAR,
    fontWeightMedium: FW_MEDIUM,
    // Semibold is the ceiling for chrome: Zoho CRM never uses true bold for
    // card and section titles, and a widget that does out-shouts its host.
    fontWeightBold: FW_SEMIBOLD,
    // Every variant is pinned to the shared scale with normal tracking — MUI
    // ships per-variant letter-spacing (button is the loudest at 0.02857em)
    // and Zoho sets none.
    h4: {
      fontSize: FS_HEADING,
      fontWeight: FW_SEMIBOLD,
      letterSpacing: "normal",
      lineHeight: LH_HEADING,
    },
    h5: {
      fontSize: FS_SECTION,
      fontWeight: FW_SEMIBOLD,
      letterSpacing: "normal",
      lineHeight: LH_HEADING,
    },
    h6: {
      fontSize: FS_TITLE,
      fontWeight: FW_SEMIBOLD,
      letterSpacing: "normal",
      lineHeight: LH_HEADING,
    },
    subtitle1: {
      fontSize: FS_SUBTITLE,
      letterSpacing: "normal",
      lineHeight: LH_BODY,
    },
    subtitle2: {
      fontSize: FS_BODY,
      fontWeight: FW_SEMIBOLD,
      letterSpacing: "normal",
      lineHeight: LH_BODY,
    },
    body1: { fontSize: FS_BODY, letterSpacing: "normal", lineHeight: LH_BODY },
    body2: { fontSize: FS_CAPTION, letterSpacing: "normal", lineHeight: LH_BODY },
    caption: { fontSize: FS_MICRO, letterSpacing: "normal", lineHeight: LH_BODY },
    button: {
      textTransform: "none",
      fontSize: FS_BODY,
      fontWeight: FW_SEMIBOLD,
      letterSpacing: "normal",
    },
  },
  components: {
    // No dimming veil behind dialogs and loaders — the widget is a panel inside
    // a CRM page, not a standalone app taking over the viewport.
    MuiBackdrop: {
      styleOverrides: { root: { backgroundColor: "transparent" } },
    },
    MuiPaper: {
      styleOverrides: {
        // MUI paints elevation as a background-image gradient on top of the
        // fill; clearing it keeps surfaces flat and predictable.
        root: { backgroundImage: "none" },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: SURFACE,
          border: CARD_BORDER,
          borderRadius: CARD_RADIUS,
          boxShadow: CARD_SHADOW,
        },
      },
    },
    MuiButton: {
      styleOverrides: { root: { borderRadius: BUTTON_RADIUS } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: BORDER_DARK },
      },
    },
  },
});

// Exported so cards can opt into the hover lift without restating the shadow.
export const CARD_HOVER_SX = {
  transition: "box-shadow 0.2s ease-in-out",
  "&:hover": { boxShadow: CARD_SHADOW_HOVER },
};

export default theme;
